<template>
  <BaseLayout :menuLinks="sideMenuLinks">
    <template v-slot>
      <!-- Modern Breadcrumb Navigation -->
      <ModernBreadcrumb :items="breadcrumbItems" />

      <!-- Standardized Header -->
      <StandardHeader
        title="Feed Setup"
        subtitle="Choose your preferred method to import and discover data"
        icon="pi pi-cog"
        :actions="headerActions"
      />

      <!-- Import Methods Selection Card -->
      <Card class="shadow-modern border-round-2xl overflow-hidden mb-4 main-content-card">
        <template #title>
          <div class="flex align-items-center justify-content-between p-3 mb-3 w-full stats-header">
            <div class="flex align-items-center">
              <div class="bg-blue-50 text-blue-600 p-2 border-round-lg mr-3">
                <i class="pi pi-cog"></i>
              </div>
              <div>
                <h2 class="text-lg font-semibold text-slate-800 m-0">Feed Setup</h2>
                <p class="text-slate-600 m-0 text-sm font-medium">Select how you want to connect your data source</p>
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <!-- Import Method Cards -->
          <div class="grid mb-4">
            <div class="col-12 md:col-4">
              <div
                  class="border-2 border-round-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-3"
                  :class="feedImportMethod?.code === 'url' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'"
                  @click="selectImportMethod('url')">
                <div class="text-center">
                  <div class="bg-blue-50 text-blue-600 p-3 border-circle inline-flex mb-3">
                    <i class="pi pi-link"></i>
                  </div>
                  <h3 class="text-lg font-semibold mb-2 text-gray-800">Import a URL</h3>
                  <div class="flex align-items-center justify-content-center">
                    <Tag
                        value="Recommended"
                        severity="success"
                        class="text-xs"
                        v-if="feedImportMethod?.code === 'url'"/>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 md:col-4">
              <div
                  class="border-2 border-round-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-3"
                  :class="feedImportMethod?.code === 'content' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'"
                  @click="selectImportMethod('content')">
                <div class="text-center">
                  <div class="bg-purple-50 text-purple-600 p-3 border-circle inline-flex mb-3">
                    <i class="pi pi-copy"></i>
                  </div>
                  <h3 class="text-lg font-semibold mb-2 text-gray-800">Paste Content</h3>
                  <div class="flex align-items-center justify-content-center">
                    <Tag
                        value="Quick Test"
                        severity="info"
                        class="text-xs"
                        v-if="feedImportMethod?.code === 'content'"/>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 md:col-4">
              <div
                  class="border-2 border-round-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-3"
                  :class="feedImportMethod?.code === 'file' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'"
                  @click="selectImportMethod('file')">
                <div class="text-center">
                  <div class="bg-orange-50 text-orange-600 p-3 border-circle inline-flex mb-3">
                    <i class="pi pi-upload"></i>
                  </div>
                  <h3 class="text-lg font-semibold mb-2 text-gray-800">Upload a local file</h3>
                  <div class="flex align-items-center justify-content-center">
                    <Tag
                        value="Local Files"
                        severity="warning"
                        class="text-xs"
                        v-if="feedImportMethod?.code === 'file'"/>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Configuration Content -->
          <div v-if="feedImportMethod" class="border-top-1 border-gray-200 pt-4">
            <!-- URL Method Configuration -->
            <div v-if="feedImportMethod.code === 'url'" class="max-w-6xl mx-auto">

              <div class="grid">
                <div class="col-12 lg:col-8">
                  <!-- Required Field First: URL -->
                  <div class="field p-fluid mb-3">
                    <label for="source-url" class="font-semibold mb-2 block">Feed URL *</label>
                    <InputGroup>
                      <InputText
                          id="source-url"
                          v-model="feedDataSource"
                          placeholder="https://api.example.com/data"
                          :class="{'p-invalid': urlError}"
                          class="text-base"/>
                      <Button
                          icon="pi pi-lock"
                          v-if="!showAuthOptions"
                          v-tooltip="'Add authentication'"
                          severity="secondary"
                          outlined
                          @click="showAuthOptions = true"/>
                    </InputGroup>
                    <small v-if="urlError" class="p-error">{{ urlError }}</small>
                  </div>

                  <!-- Authentication Panel -->
                  <div v-if="showAuthOptions" class="auth-panel mb-3">
                    <div class="auth-panel-header">
                      <div class="auth-panel-title">
                        <div class="auth-icon">
                          <i class="pi pi-shield"></i>
                        </div>
                        <div>
                          <h3 class="m-0 text-lg font-semibold text-gray-800">Authentication Setup</h3>
                          <p class="m-0 text-sm text-gray-600">Secure your API connection</p>
                        </div>
                      </div>
                      <Button
                          icon="pi pi-times"
                          size="small"
                          severity="secondary"
                          outlined
                          class="auth-close-btn"
                          v-tooltip="'Close authentication setup'"
                          @click="cancelAuth"/>
                    </div>

                    <div class="auth-panel-content">
                      <div class="field p-fluid mb-4">
                        <label for="auth-method" class="font-semibold mb-2 block text-gray-700">
                          <i class="pi pi-key mr-2 text-blue-600"></i>
                          Authentication Method
                        </label>
                        <Dropdown
                            id="auth-method"
                            v-model="authMethod"
                            :options="authMethods"
                            optionLabel="name"
                            optionValue="code"
                            placeholder="🔒 Choose authentication type..."
                            class="auth-dropdown"
                            filter
                            filterPlaceholder="Search authentication methods..."
                            @change="setupAuthFields">
                          <template #value="slotProps">
                            <div v-if="slotProps.value" class="flex align-items-center">
                              <i :class="getAuthMethodIcon(slotProps.value)" class="mr-2"></i>
                              <span>{{ getAuthMethodName(slotProps.value) }}</span>
                            </div>
                            <span v-else class="text-gray-500">{{ slotProps.placeholder }}</span>
                          </template>
                          <template #option="slotProps">
                            <div class="flex align-items-center auth-option">
                              <i :class="getAuthMethodIcon(slotProps.option.code)" class="mr-2"></i>
                              <div>
                                <div class="font-medium">{{ slotProps.option.name }}</div>
                                <small class="text-gray-500">{{ getAuthMethodDescription(slotProps.option.code) }}</small>
                              </div>
                            </div>
                          </template>
                        </Dropdown>
                      </div>

                      <!-- Basic Auth -->
                      <div v-if="authMethod === 'basic'" class="grid">
                        <div class="col-12 md:col-6">
                          <div class="field p-fluid">
                            <label for="auth-username" class="font-semibold mb-2 block">Username</label>
                            <InputText id="auth-username" v-model="auth.username" placeholder="Enter username"/>
                          </div>
                        </div>
                        <div class="col-12 md:col-6">
                          <div class="field p-fluid">
                            <label for="auth-password" class="font-semibold mb-2 block">Password</label>
                            <Password
                                id="auth-password"
                                v-model="auth.password"
                                placeholder="Enter password"
                                :feedback="false"
                                :toggleMask="true"/>
                          </div>
                        </div>
                      </div>

                      <!-- Bearer Token -->
                      <div v-if="authMethod === 'bearer'">
                        <div class="field p-fluid">
                          <label for="auth-token" class="font-semibold mb-2 block">Bearer Token</label>
                          <Textarea
                              id="auth-token"
                              v-model="auth.bearer_token"
                              rows="3"
                              placeholder="paste-your-bearer-token-here"/>
                        </div>
                      </div>

                      <!-- API Key -->
                      <div v-if="authMethod === 'api_key'" class="grid">
                        <div class="col-12 md:col-6">
                          <div class="field p-fluid">
                            <label for="auth-key-name" class="font-semibold mb-2 block">Header Name</label>
                            <InputText
                                id="auth-key-name"
                                v-model="auth.key_name"
                                placeholder="X-API-Key"/>
                          </div>
                        </div>
                        <div class="col-12 md:col-6">
                          <div class="field p-fluid">
                            <label for="auth-key-value" class="font-semibold mb-2 block">API Key</label>
                            <Password
                                id="auth-key-value"
                                v-model="auth.key_value"
                                placeholder="Enter your API key"
                                :feedback="false"
                                :toggleMask="true"/>
                          </div>
                        </div>
                      </div>

                      <!-- OAuth2 -->
                      <div v-if="authMethod === 'oauth2'" class="grid">
                        <div class="col-12 md:col-6">
                          <div class="field p-fluid mb-3">
                            <label for="auth-client-id" class="font-semibold mb-2 block">Client ID</label>
                            <InputText id="auth-client-id" v-model="auth.client_id" placeholder="Enter client ID"/>
                          </div>
                        </div>
                        <div class="col-12 md:col-6">
                          <div class="field p-fluid mb-3">
                            <label for="auth-client-secret" class="font-semibold mb-2 block">Client Secret</label>
                            <Password
                                id="auth-client-secret"
                                v-model="auth.client_secret"
                                placeholder="Enter client secret"
                                :feedback="false"
                                :toggleMask="true"/>
                          </div>
                        </div>
                        <div class="col-12">
                          <div class="field p-fluid mb-3">
                            <label for="auth-token-url" class="font-semibold mb-2 block">Token URL</label>
                            <InputText id="auth-token-url" v-model="auth.token_url"
                                       placeholder="https://example.com/oauth/token"/>
                          </div>
                        </div>
                        <div class="col-12">
                          <div class="field p-fluid">
                            <label for="auth-scopes" class="font-semibold mb-2 block">Scopes</label>
                            <Chips id="auth-scopes" v-model="scopes" placeholder="Add scope and press Enter"/>
                          </div>
                        </div>
                      </div>

                      <div class="auth-buttons-container">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            class="auth-btn"
                            @click="cancelAuth"/>
                        <Button
                            label="Apply Authentication"
                            icon="pi pi-check"
                            severity="success"
                            class="auth-btn"
                            @click="showAuthOptions = false"/>
                      </div>
                    </div>
                  </div>

                  <!-- Feed Information -->
                  <div class="field p-fluid mb-3">
                    <label for="feed-name" class="font-semibold mb-2 block">Feed Name</label>
                    <InputText
                        id="feed-name"
                        v-model="dataSource.name"
                        placeholder="My API Feed"
                        class="text-base"/>
                  </div>

                  <div class="field p-fluid mb-3">
                    <label for="feed-description" class="font-semibold mb-2 block">Description</label>
                    <Textarea
                        id="feed-description"
                        v-model="description"
                        placeholder="Describe your feed..."
                        rows="2"
                        class="text-base"/>
                  </div>
                </div>

                <div class="col-12 lg:col-4">
                  <div class="bg-gradient-to-br from-blue-50 to-indigo-100 p-4 border-round-lg">
                    <h4 class="text-lg font-semibold mb-3 text-gray-800">
                      <i class="pi pi-lightbulb mr-2 text-yellow-500"></i>
                      Quick Tips
                    </h4>
                    <ul class="list-none p-0 m-0">
                      <li class="flex align-items-start mb-2">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Use full URLs including protocol (https://)</span>
                      </li>
                      <li class="flex align-items-start mb-2">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Authentication is optional for public APIs</span>
                      </li>
                      <li class="flex align-items-start">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Supports JSON, XML, CSV, and other formats</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Discovery Button -->
              <div class="text-center mt-4">
                <Button
                    @click="parseFeedModels"
                    :loading="loadingModels"
                    icon="pi pi-plus"
                    label="Create the feed"
                    severity="success"
                    class="px-6 py-3"
                    :disabled="!feedDataSource"/>
              </div>
            </div>

            <!-- Paste Content Method -->
            <div v-if="feedImportMethod.code === 'content'" class="max-w-6xl mx-auto">

              <div class="grid">
                <div class="col-12 lg:col-8">
                  <!-- Required Field First: Content -->
                  <div class="field p-fluid mb-3">
                    <label for="paste-content" class="font-semibold mb-2 block">Data Content *</label>
                    <Textarea
                        id="paste-content"
                        v-model="feedDataSource"
                        placeholder="Paste your JSON, XML, CSV or other structured data here..."
                        rows="20"
                        class="w-full font-mono text-sm"
                        style="resize: vertical;"/>
                  </div>

                  <!-- Feed Information -->
                  <div class="field p-fluid mb-3">
                    <label for="feed-name-content" class="font-semibold mb-2 block">Feed Name</label>
                    <InputText
                        id="feed-name-content"
                        v-model="dataSource.name"
                        placeholder="My Content Feed"
                        class="text-base"/>
                  </div>

                  <div class="field p-fluid">
                    <label for="feed-description-content" class="font-semibold mb-2 block">Description</label>
                    <Textarea
                        id="feed-description-content"
                        v-model="description"
                        placeholder="Describe your feed..."
                        rows="2"
                        class="text-base"/>
                  </div>
                </div>

                <div class="col-12 lg:col-4">
                  <div class="bg-gradient-to-br from-purple-50 to-pink-100 p-4 border-round-lg">
                    <h4 class="text-lg font-semibold mb-3 text-gray-800">
                      <i class="pi pi-lightbulb mr-2 text-yellow-500"></i>
                      Quick Tips
                    </h4>
                    <ul class="list-none p-0 m-0">
                      <li class="flex align-items-start mb-2">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Paste structured data directly</span>
                      </li>
                      <li class="flex align-items-start mb-2">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Supports JSON, XML, CSV, and other formats</span>
                      </li>
                      <li class="flex align-items-start">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Perfect for quick testing</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Discovery Button -->
              <div class="text-center mt-4">
                <Button
                    @click="parseFeedModels"
                    :loading="loadingModels"
                    icon="pi pi-plus"
                    label="Create the feed"
                    severity="success"
                    class="px-6 py-3"
                    :disabled="!feedDataSource || feedDataSource.trim() === ''"/>
              </div>
            </div>

            <!-- File Upload Method -->
            <div v-if="feedImportMethod.code === 'file'" class="max-w-4xl mx-auto">

              <div class="grid">
                <div class="col-12 lg:col-8">
                  <!-- Required Field First: File Upload -->
                  <div
                      class="border-2 border-dashed border-gray-300 border-round-lg p-6 hover:border-orange-400 transition-colors mb-3">
                    <FileUpload
                        ref="feedUpload"
                        name="datasource"
                        mode="basic"
                        @select="selectFeedFile($event)"
                        @remove="resetViewProperties"
                        choose-icon="pi pi-upload"
                        choose-label="Choose File *"
                        :maxFileSize="1000000000"
                        :auto="false"
                        accept=".json,.xml,.csv,.txt,.ion">
                      <template #empty>
                        <div class="text-center">
                          <div class="bg-orange-50 text-orange-600 p-4 border-circle inline-flex mb-4">
                            <i class="pi pi-cloud-upload"></i>
                          </div>
                          <h3 class="text-xl font-semibold mb-2 text-gray-800">Upload Your Data File *</h3>
                          <p class="text-gray-600 mb-3">Drag and drop your file here or click to browse</p>
                          <div class="flex justify-content-center gap-2 flex-wrap">
                            <Tag value="JSON" severity="info"/>
                            <Tag value="XML" severity="warning"/>
                            <Tag value="CSV" severity="success"/>
                            <Tag value="TXT" severity="secondary"/>
                          </div>
                          <small class="text-gray-500 mt-2 block">Maximum file size: 1GB</small>
                        </div>
                      </template>
                    </FileUpload>
                  </div>

                  <!-- Feed Information -->
                  <div class="field p-fluid mb-3">
                    <label for="feed-name-file" class="font-semibold mb-2 block">Feed Name</label>
                    <InputText
                        id="feed-name-file"
                        v-model="dataSource.name"
                        placeholder="My File Feed"
                        class="text-base"/>
                  </div>

                  <div class="field p-fluid mb-3">
                    <label for="feed-description-file" class="font-semibold mb-2 block">Description</label>
                    <Textarea
                        id="feed-description-file"
                        v-model="description"
                        placeholder="Describe your feed..."
                        rows="2"
                        class="text-base"/>
                  </div>
                </div>

                <div class="col-12 lg:col-4">
                  <div class="bg-gradient-to-br from-orange-50 to-red-100 p-4 border-round-lg">
                    <h4 class="text-lg font-semibold mb-3 text-gray-800">
                      <i class="pi pi-lightbulb mr-2 text-yellow-500"></i>
                      Quick Tips
                    </h4>
                    <ul class="list-none p-0 m-0">
                      <li class="flex align-items-start mb-2">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Maximum file size: 1GB</span>
                      </li>
                      <li class="flex align-items-start mb-2">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Files are processed locally</span>
                      </li>
                      <li class="flex align-items-start">
                        <i class="pi pi-check-circle text-green-500 mr-2 mt-1 text-sm"></i>
                        <span class="text-sm text-gray-700">Supports JSON, XML, CSV, and other formats</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Discovery Button -->
              <div class="text-center mt-4">
                <Button
                    @click="parseFeedModels"
                    :loading="loadingModels"
                    icon="pi pi-plus"
                    label="Create the feed"
                    severity="success"
                    class="px-6 py-3"
                    :disabled="!feedDataSource"/>
              </div>

            </div>
          </div>
        </template>
      </Card>

      <!-- Models and data display removed - now redirects to FeedDetail discovery tab -->

      <!-- Dialog removed - name/description now directly in form -->

      <Toast/>
    </template>
  </BaseLayout>
</template>

<script setup>
import {nextTick, onMounted, reactive, ref} from 'vue';
import {useRouter} from 'vue-router';
import {useStore} from 'vuex';
import Dropdown from "primevue/dropdown";
import InputGroup from "primevue/inputgroup";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Button from "primevue/button";
import FileUpload from "primevue/fileupload";
import Toast from "primevue/toast";
import Card from "primevue/card";
import Password from "primevue/password";
import Chips from "primevue/chips";
import Tag from "primevue/tag";
import {useToast} from "primevue/usetoast";
import apiService from "@/services/apiService";
import BaseLayout from "@/components/BaseLayout.vue";
import ModernBreadcrumb from '@/components/ModernBreadcrumb.vue';
import StandardHeader from '@/components/StandardHeader.vue';
import {menuLinks} from '@/shared/sharedLinks.js';

const router = useRouter();
const store = useStore();

// Mobile-first responsive design - using CSS breakpoints only

const toast = useToast();

// Layout & Navigation
const sideMenuLinks = ref(menuLinks);

// Breadcrumb navigation
const breadcrumbItems = ref([
  {
    icon: 'pi pi-home',
    label: 'My Feeds',
    route: '/feeds'
  },
  {
    icon: 'pi pi-cog',
    label: 'Feed Setup',
    route: null // Current page, no route
  }
]);

// Header actions
import { computed } from 'vue';

const headerActions = computed(() => [
  {
    icon: 'pi pi-list',
    label: 'My Feeds',
    mobileLabel: '',
    size: 'small',
    mobileSize: 'large',
    severity: 'secondary',
    outlined: true,
    tooltip: 'My Feeds',
    command: () => router.push('/feeds')
  }
]);

// Check authentication on component mount
onMounted(async () => {
  console.log('Component mounted, checking authentication...');
  console.log('Is authenticated:', store.getters.isAuthenticated);
  console.log('Token from store:', store.getters.token);
  console.log('Token from localStorage:', localStorage.getItem('token'));

  // Fetch current user if authenticated
  if (store.getters.isAuthenticated) {
    try {
      console.log('Fetching current user...');
      await store.dispatch('fetchCurrentUser');
      console.log('User fetch successful');
    } catch (error) {
      console.error('User fetch error:', error);
    }
  }
});

// Upload headers function removed - now using unified approach


// Data Source
const dataSource = reactive({
  name: '',
  kind: 'rest_api',
  connection: {
    provider: 'generic_rest',
    url: '',
    options: {}
  }
});
const description = ref('');
const savedFeed = ref(false);
const savedFeedId = ref(null);

// Feed Import
const feedDataSource = ref();
const feedImportMethod = ref();
const feedImportMethods = ref([
  {name: 'URL', code: 'url'},
  {name: 'Paste Content', code: 'content'},
  {name: 'File Upload', code: 'file'},
]);
const feedUpload = ref();
const urlError = ref('');
// nameError removed - validation now handled differently


// Authentication
const showAuthOptions = ref(false);
const authMethod = ref(null);
const scopes = ref([]);
const auth = reactive({
  method: null,
  username: '',
  password: '',
  bearer_token: '',
  key_name: '',
  key_value: '',
  client_id: '',
  client_secret: '',
  token_url: '',
  scopes: []
});

const authMethods = ref([
  {name: 'No Authentication', code: 'none'},
  {name: 'Basic Auth', code: 'basic'},
  {name: 'Bearer Token', code: 'bearer'},
  {name: 'API Key', code: 'api_key'},
  {name: 'OAuth 2.0', code: 'oauth2'}
]);

// Model Data - most variables removed, only keeping essentials
const loadingModels = ref(false);

// Computed property to count active filters
// activeFiltersCount removed - no longer needed
const selectedModel = ref();
// selectedModelFields removed - not needed for redirect approach
const showModelFieldValuesView = ref(false);
const showModelView = ref(false);
const showModelsView = ref(false);

// Dialog variables removed - now using inline form fields


// Setup Auth fields when auth method changes
const setupAuthFields = () => {
  auth.method = authMethod.value;

  // Reset fields not relevant to the current auth method
  if (authMethod.value !== 'basic') {
    auth.username = '';
    auth.password = '';
  }

  if (authMethod.value !== 'bearer') {
    auth.bearer_token = '';
  }

  if (authMethod.value !== 'api_key') {
    auth.key_name = '';
    auth.key_value = '';
  }

  if (authMethod.value !== 'oauth2') {
    auth.client_id = '';
    auth.client_secret = '';
    auth.token_url = '';
    scopes.value = [];
  }
};

// Cancel auth setup
const cancelAuth = () => {
  authMethod.value = null;
  auth.method = null;
  auth.username = '';
  auth.password = '';
  auth.bearer_token = '';
  auth.key_name = '';
  auth.key_value = '';
  auth.client_id = '';
  auth.client_secret = '';
  auth.token_url = '';
  scopes.value = [];
  showAuthOptions.value = false;
};

// Get auth method icon
const getAuthMethodIcon = (code) => {
  const icons = {
    'none': 'pi pi-unlock text-gray-500',
    'basic': 'pi pi-user text-blue-600',
    'bearer': 'pi pi-key text-green-600',
    'api_key': 'pi pi-key text-orange-600',
    'oauth2': 'pi pi-shield text-purple-600'
  };
  return icons[code] || 'pi pi-cog';
};

// Get auth method name
const getAuthMethodName = (code) => {
  const method = authMethods.value.find(m => m.code === code);
  return method ? method.name : '';
};

// Get auth method description
const getAuthMethodDescription = (code) => {
  const descriptions = {
    'none': 'No authentication required',
    'basic': 'Username and password authentication',
    'bearer': 'Token-based authentication',
    'api_key': 'Custom header with API key',
    'oauth2': 'OAuth 2.0 flow authentication'
  };
  return descriptions[code] || '';
};

// prepareAuthForApi function removed - auth now handled in parseFeedModels

// Copy field values
// copyModelFieldValues removed - no longer needed

// scrollToElementPosition removed

// Model field functions removed - now using FeedDetail discovery

// Filter functions removed - now handled in FeedDetail discovery

// Clear filter functions removed

// Reset view properties
const resetViewProperties = () => {
  console.log("In reset user selections");
  showModelView.value = false;
  showModelsView.value = false;
  showModelFieldValuesView.value = false;
  selectedModel.value = undefined;
  savedFeed.value = false;
  savedFeedId.value = null;
};

// File upload functions removed - now using unified parseFeedModels approach

// Remplacer selectFeedImportMethod par cette nouvelle fonction
const selectImportMethod = (methodCode) => {
  // Trouver la méthode correspondante
  const method = feedImportMethods.value.find(m => m.code === methodCode);

  if (method) {
    feedImportMethod.value = method;
    console.log("Selected import method:", method);

    // Reset les propriétés
    resetViewProperties();
    feedDataSource.value = '';
    showAuthOptions.value = false;
    urlError.value = '';

    // Reset auth
    cancelAuth();


  }
  nextTick(() => {
    const configSection = document.querySelector('.border-top-1.border-gray-200');
    if (configSection) {
      configSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  });

};

// Supprimer ou commenter l'ancienne fonction selectFeedImportMethod
// const selectFeedImportMethod = (event) => {
//   console.log("In select feed import method: ", event);
//   resetViewProperties();
//   feedDataSource.value = undefined;
//   showAuthOptions.value = false;
// };

const selectFeedFile = (event) => {
  console.log("In Select feed file: ", event);
  feedDataSource.value = event.files[0];
};

// selectPreferredModel function removed - now using unified approach

// scrollToModelView function removed - redirect approach doesn't need scrolling

const parseFeedModels = async () => {
  // Validate inputs based on import method
  urlError.value = '';

  if (feedImportMethod.value.code === 'url') {
    if (!feedDataSource.value) {
      urlError.value = 'URL is required';
      return;
    }
  } else if (feedImportMethod.value.code === 'content') {
    if (!feedDataSource.value || feedDataSource.value.trim() === '') {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Content is required',
        life: 3000
      });
      return;
    }
  } else if (feedImportMethod.value.code === 'file') {
    if (!feedDataSource.value) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'File is required',
        life: 3000
      });
      return;
    }
  }

  console.log("Creating feed and redirecting to discovery");
  console.log(`Feed datasource: ${feedDataSource.value}`);
  console.log(`Import method: ${feedImportMethod.value.code}`);
  
  loadingModels.value = true;
  resetViewProperties();

  // Prepare dataSource based on import method
  if (feedImportMethod.value.code === 'url') {
    // URL method - set connection URL and auth
    dataSource.connection.url = feedDataSource.value;
    dataSource.kind = 'rest_api';
    dataSource.connection.provider = 'generic_rest';
    
    // Set authentication data if provided
    if (authMethod.value && authMethod.value !== 'none') {
      // Format auth according to AppAuth structure (method + fields directly)
      dataSource.connection.auth = {
        method: authMethod.value
      };
      
      switch (authMethod.value) {
        case 'basic':
          dataSource.connection.auth.username = auth.username;
          dataSource.connection.auth.password = auth.password;
          break;
        case 'bearer':
          dataSource.connection.auth.bearer_token = auth.bearer_token;
          break;
        case 'api_key':
          dataSource.connection.auth.key_name = auth.key_name;
          dataSource.connection.auth.key_value = auth.key_value;
          break;
        case 'oauth2':
          dataSource.connection.auth.client_id = auth.client_id;
          dataSource.connection.auth.client_secret = auth.client_secret;
          dataSource.connection.auth.token_url = auth.token_url;
          dataSource.connection.auth.scopes = [...scopes.value];
          break;
      }
    } else {
      dataSource.connection.auth = null;
    }
    
  }
  
  try {
    let saveResponse;
    
    if (feedImportMethod.value.code === 'content') {
      // Content method - use specialized content API
      console.log("Saving content feed");
      // Use provided name or generate default
      const feedName = dataSource.name || 'Content Feed';
      saveResponse = await apiService.createContentSource(
        feedName,
        description.value || '',
        feedDataSource.value
      );
      
    } else if (feedImportMethod.value.code === 'file') {
      // File method - use specialized file API
      console.log("Saving file feed");
      // Use provided name or generate default from file name
      const feedName = dataSource.name || feedDataSource.value.name || 'Uploaded File Feed';
      saveResponse = await apiService.createFileSource(
        feedName,
        description.value || '',
        feedDataSource.value // This is the File object
      );
      
    } else {
      // URL method or other - use standard API
      // Add description if provided
      if (description.value) {
        dataSource.description = description.value;
      }
      
      // Use provided name or generate default from URL
      if (!dataSource.name) {
        try {
          const url = new URL(feedDataSource.value);
          dataSource.name = url.hostname || 'URL Feed';
        } catch {
          dataSource.name = 'URL Feed';
        }
      }
      
      console.log("Saving URL feed:", dataSource);
      saveResponse = await apiService.createSource(dataSource);
    }
    
    console.log("Feed saved:", saveResponse);
    
    savedFeed.value = true;
    savedFeedId.value = saveResponse.data_source?.uid;
    
    // Show success message
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Feed created successfully! Redirecting to discovery...',
      life: 3000
    });
    
    // Redirect to feed detail page with discovery tab selected
    await router.push(`/feeds/${savedFeedId.value}?tab=discovery`);
    
  } catch (error) {
    console.error('Error creating feed:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.message || 'Failed to create feed',
      life: 5000
    });
  } finally {
    loadingModels.value = false;
  }


};

// 2. Ajouter une fonction pour valider et demander le nom lors de la sauvegarde

// Dialog-related functions removed - name/description now directly in form

// parseFeedModelStructureAndItems function removed - now handled in FeedDetail discovery


// All model-related initialization removed - now using redirect approach

</script>

<style scoped>
/* Mobile card styles */
.field-card {
  background-color: white;
  transition: transform 0.2s, box-shadow 0.2s;
}

.field-card:active {
  transform: translateY(1px);
}

.field-card-header {
  border-bottom: 1px solid #f0f0f0;
}

.field-card-body {
  background-color: white;
}

/* Break long text */
.break-all {
  word-break: break-all;
  white-space: normal;
}
</style>

<style scoped>
/* Modern Hero Section */
.hero-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: #1f2937;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.hero-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
}

.hero-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.hero-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 8px 16px rgba(59, 130, 246, 0.3);
}

.hero-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.025em;
}

.hero-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  font-weight: 400;
}

.hero-actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

/* Card Styles */
.main-content-card {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.shadow-modern {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.border-round-2xl {
  border-radius: 1rem;
}

.stats-header {
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb !important;
  width: 100% !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

/* Authentication Panel Styles */
.auth-panel {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.auth-panel-header {
  background: linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%);
  border-bottom: 1px solid #dbeafe;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.auth-panel-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.auth-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.auth-panel-content {
  padding: 1.5rem;
  background: white;
}

.auth-dropdown {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.auth-dropdown:hover {
  border-color: #3b82f6;
}

.auth-dropdown:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.auth-option {
  padding: 0.5rem 0;
  transition: all 0.2s ease;
}

.auth-option:hover {
  background-color: #f8fafc;
  border-radius: 6px;
}

.auth-btn {
  transition: all 0.2s ease;
  font-weight: 500;
}

/* Mobile First - Auth buttons container */
.auth-buttons-container {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap; /* Mobile: allow wrapping to prevent truncation */
}

.auth-buttons-container .auth-btn {
  flex: 1 1 auto; /* Mobile: buttons take equal space */
  min-width: fit-content;
  white-space: nowrap;
  font-size: 0.875rem; /* Mobile: smaller text */
  padding: 0.5rem 0.75rem; /* Mobile: compact padding */
}

.auth-close-btn {
  transition: all 0.2s ease;
}

.auth-close-btn:hover {
  background-color: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

/* Mobile First - FeedSetup optimized for iPhone SE (375x667) */
.main-content-card {
  padding: 0.5rem; /* Mobile: compact card padding */
}

.stats-header {
  padding: 0.75rem !important; /* Mobile: compact header */
}

.stats-header h2 {
  font-size: 1rem !important; /* Mobile: smaller title */
}

.stats-header p {
  font-size: 0.75rem !important; /* Mobile: smaller subtitle */
}

/* Import method cards - mobile first */
.grid .col-12.md\\:col-4 {
  margin-bottom: 0.75rem; /* Mobile: gap between cards */
}

.grid .col-12.md\\:col-4:last-child {
  margin-bottom: 0; /* Mobile: no margin on last card */
}

/* Import method cards content */
.grid .col-12.md\\:col-4 h3 {
  font-size: 1rem; /* Mobile: smaller card titles */
}

.grid .col-12.md\\:col-4 .p-3 {
  padding: 0.75rem !important; /* Mobile: smaller icon containers */
}

/* Form sections mobile first */
.max-w-6xl, .max-w-4xl {
  max-width: none; /* Mobile: full width */
  padding: 0 0.5rem; /* Mobile: side padding */
}

.grid .col-12.lg\\:col-8 {
  padding: 0 0.5rem; /* Mobile: compact form padding */
}

.grid .col-12.lg\\:col-4 {
  padding: 0 0.5rem; /* Mobile: compact tips padding */
  margin-top: 1rem; /* Mobile: tips below form */
}

/* Quick tips section mobile */
.bg-gradient-to-br h4 {
  font-size: 1rem; /* Mobile: smaller tips title */
}

.bg-gradient-to-br span {
  font-size: 0.75rem; /* Mobile: smaller tips text */
}

/* Authentication panel mobile */
.auth-panel-header {
  padding: 0.75rem !important; /* Mobile: compact auth header */
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
}

.auth-panel-content {
  padding: 0.75rem !important; /* Mobile: compact auth content */
}

.auth-panel-title {
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
}

/* Form fields mobile */
.field label {
  font-size: 0.875rem; /* Mobile: smaller labels */
  margin-bottom: 0.5rem !important;
}

.p-inputtext, .p-textarea, .p-dropdown {
  font-size: 0.875rem !important; /* Mobile: smaller input text */
}

/* Mobile First - Consistent placeholder styling */
.p-inputtext::placeholder,
.p-textarea::placeholder,
.p-password input::placeholder,
.p-chips input::placeholder {
  font-size: 0.875rem !important; /* Mobile: consistent placeholder size */
  color: #9ca3af;
}

/* Buttons mobile */
.px-6.py-3 {
  padding: 0.75rem 1.5rem !important; /* Mobile: compact button padding */
  font-size: 0.875rem !important; /* Mobile: smaller button text */
}

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .main-content-card {
    padding: 0.75rem;
  }

  .stats-header {
    padding: 1rem !important;
  }

  .stats-header h2 {
    font-size: 1.125rem !important;
  }

  .stats-header p {
    font-size: 0.875rem !important;
  }

  .grid .col-12.md\\:col-4 h3 {
    font-size: 1.125rem;
  }

  .max-w-6xl, .max-w-4xl {
    padding: 0 0.75rem;
  }

  .auth-panel-header {
    padding: 1rem !important;
    flex-direction: row;
    text-align: left;
  }

  .auth-panel-content {
    padding: 1rem !important;
  }

  .auth-panel-title {
    flex-direction: row;
    text-align: left;
  }

  /* Small tablet: auth buttons improvement */
  .auth-buttons-container {
    flex-wrap: nowrap; /* Small tablet: keep buttons on same line */
  }

  .auth-buttons-container .auth-btn {
    flex: 0 1 auto; /* Small tablet: buttons natural width */
    font-size: 1rem;
    padding: 0.75rem 1rem;
  }
}

/* ===== MEDIUM TABLET (768px+) ===== */
@media (min-width: 768px) {
  .main-content-card {
    padding: 1rem;
  }

  .stats-header {
    padding: 1.25rem !important;
  }

  .stats-header h2 {
    font-size: 1.25rem !important;
  }

  .stats-header p {
    font-size: 1rem !important;
  }

  .grid .col-12.md\\:col-4 {
    margin-bottom: 0;
  }

  .grid .col-12.md\\:col-4 h3 {
    font-size: 1.25rem;
  }

  .max-w-6xl, .max-w-4xl {
    padding: 0 1rem;
  }

  .grid .col-12.lg\\:col-4 {
    margin-top: 0;
  }

  .auth-panel-header {
    padding: 1.25rem !important;
  }

  .auth-panel-content {
    padding: 1.25rem !important;
  }

  .field label {
    font-size: 1rem;
    margin-bottom: 0.75rem !important;
  }

  .p-inputtext, .p-textarea, .p-dropdown {
    font-size: 1rem !important;
  }

  /* Medium tablet: consistent placeholder styling */
  .p-inputtext::placeholder,
  .p-textarea::placeholder,
  .p-password input::placeholder,
  .p-chips input::placeholder {
    font-size: 1rem !important;
  }

  .px-6.py-3 {
    padding: 1rem 2rem !important;
    font-size: 1rem !important;
  }
}

/* ===== LARGE DESKTOP (992px+) ===== */
@media (min-width: 992px) {
  .main-content-card {
    padding: 1.5rem;
  }

  .stats-header {
    padding: 1.5rem !important;
  }

  .max-w-6xl {
    max-width: 72rem;
  }

  .max-w-4xl {
    max-width: 56rem;
  }

  .auth-panel-header {
    padding: 1.5rem !important;
  }

  .auth-panel-content {
    padding: 1.5rem !important;
  }

  .px-6.py-3 {
    padding: 1.25rem 2.5rem !important;
  }
}

/* CSS personnalisé pour les gradients Tailwind */
.bg-gradient-to-r {
  background: linear-gradient(to right, var(--from-color), var(--to-color));
}

.from-emerald-600 {
  --from-color: #059669;
}

.to-teal-600 {
  --to-color: #0d9488;
}

.from-gray-50 {
  --from-color: #f9fafb;
}

.to-emerald-50 {
  --to-color: #ecfdf5;
}

.from-gray-50 {
  --from-color: #f9fafb;
}

.to-teal-50 {
  --to-color: #f0fdfa;
}

.from-gray-50 {
  --from-color: #f9fafb;
}

.to-blue-50 {
  --to-color: #eff6ff;
}

.from-blue-500 {
  --from-color: #3b82f6;
}

.to-indigo-600 {
  --to-color: #4f46e5;
}

.from-purple-500 {
  --from-color: #8b5cf6;
}

.to-pink-600 {
  --to-color: #db2777;
}

.from-green-500 {
  --from-color: #10b981;
}

.to-emerald-600 {
  --to-color: #059669;
}

.from-orange-500 {
  --from-color: #f97316;
}

.to-red-600 {
  --to-color: #dc2626;
}

/* Classes utilitaires supplémentaires */
.bg-opacity-20 {
  background-color: rgba(255, 255, 255, 0.2);
}

.bg-opacity-10 {
  background-color: rgba(255, 255, 255, 0.1);
}

.backdrop-blur {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.border-left-3 {
  border-left: 3px solid;
}

.transition-all {
  transition: all 0.3s ease;
}

.duration-200 {
  transition-duration: 200ms;
}

.hover\:shadow-3:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.hover\:shadow-4:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.shadow-2 {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

</style>
